import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getClinicInfo, getSchedule, resolveMediaUrl } from '../lib/api';
import { applyThemeFromClinicInfo } from '../lib/themeColors';

const PIZAS_MODE = process.env.REACT_APP_PIZAS_MODE === '1';

export const DEFAULT_PUBLIC_LOGO = PIZAS_MODE
  ? `${process.env.PUBLIC_URL || ''}/favicon.png`
  : 'https://customer-assets.emergentagent.com/job_b4b171bf-a957-41aa-a103-fc3ac15c898d/artifacts/jwcnyg2y_logo_Podologo.png';

/** Título inicial de pestaña antes de cargar la API */
export const DEFAULT_PAGE_TITLE = PIZAS_MODE ? 'Pizzas' : 'PodoclinicAM';

function iconMimeFromHref(href) {
  const path = href.split(/[?#]/)[0].toLowerCase();
  if (path.endsWith('.svg')) return 'image/svg+xml';
  if (path.endsWith('.webp')) return 'image/webp';
  if (path.endsWith('.jpg') || path.endsWith('.jpeg')) return 'image/jpeg';
  if (path.endsWith('.gif')) return 'image/gif';
  return 'image/png';
}

function setBrowserTabIcon(iconHref) {
  const mime = iconMimeFromHref(iconHref);

  let icon = document.querySelector("link[rel='icon']");
  if (!icon) {
    icon = document.createElement('link');
    icon.rel = 'icon';
    document.head.appendChild(icon);
  }
  icon.href = iconHref;
  icon.type = mime;

  let shortcut = document.querySelector("link[rel='shortcut icon']");
  if (!shortcut) {
    shortcut = document.createElement('link');
    shortcut.rel = 'shortcut icon';
    document.head.appendChild(shortcut);
  }
  shortcut.href = iconHref;
  shortcut.type = mime;

  let apple = document.querySelector("link[rel='apple-touch-icon']");
  if (!apple) {
    apple = document.createElement('link');
    apple.rel = 'apple-touch-icon';
    document.head.appendChild(apple);
  }
  apple.href = iconHref;
}

const ClinicBrandingContext = createContext(null);

export function ClinicBrandingProvider({ children }) {
  const [info, setInfo] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [data, sched] = await Promise.all([
        getClinicInfo(),
        getSchedule().catch(() => null),
      ]);
      setInfo(data);
      setSchedule(sched);
      setError(null);
    } catch (e) {
      setError(e);
      setInfo(null);
      setSchedule(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const title = info?.name?.trim() ? info.name.trim() : DEFAULT_PAGE_TITLE;
    document.title = title;
    applyThemeFromClinicInfo(info);

    const rawLogoSrc = clinicLogo(info);
    const iconHref = resolveMediaUrl(rawLogoSrc);
    if (iconHref) {
      try {
        setBrowserTabIcon(iconHref);
      } catch {
        /* fallback: algunos navegadores bloquean data: u orígenes raros */
      }
    }
  }, [info]);

  return (
    <ClinicBrandingContext.Provider value={{ info, schedule, loading, error, refresh }}>
      {children}
    </ClinicBrandingContext.Provider>
  );
}

export function useClinicBranding() {
  const ctx = useContext(ClinicBrandingContext);
  if (!ctx) {
    throw new Error('useClinicBranding debe usarse dentro de ClinicBrandingProvider');
  }
  return ctx;
}

export function clinicLogo(info) {
  const u = info?.logo_url;
  return u && String(u).trim() !== '' ? u : DEFAULT_PUBLIC_LOGO;
}
